import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ModalDialog } from 'components/ModalDialog';
import { FormRow, Input, PercentAdornment } from 'modules/core/components';
import { MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import { ColumnWidth } from 'modules/core/enums';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { requiredValidator, validateNumberValue } from 'modules/core/validators';
import { ILenderSettingsData } from 'modules/core/types';
import { FEES_AND_TAXES_FIELD_NAMES } from 'modules/core/shared';
import { incrementResetCount } from 'store/reducers/lenders.reducer';
import { setUserLenderSettings } from 'store/reducers/auth.reducer';
import { SALES_TAX_RATE_CONFIRMED_KEY } from 'modules/auth/constants/auth.const';
import { lenderApi } from 'api';

import { Panel } from '../commonStyles';
import { LENDER_SETTINGS_FIELDS_NAMES } from './consts/fieldNames.const';

const hasValidTaxRate = (taxRate?: number | null): taxRate is number =>
  typeof taxRate === 'number' && !Number.isNaN(taxRate);

const isSalesTaxRateConfirmed = (): boolean => sessionStorage.getItem(SALES_TAX_RATE_CONFIRMED_KEY) === 'true';

export const SetupTaxRateModal: FC<{
  userLenderSettings?: ILenderSettingsData | null;
  onSave: Function;
  isReady?: boolean;
}> = ({ userLenderSettings, onSave, isReady = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setValue: commonSetValue, getValues: commonGetValues } = useFormContext();
  const formMethods = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: { lenderSettingsForm: { taxRate: NaN } },
  });
  const { handleSubmit, getValues, setValue } = formMethods;

  const savedTaxRate = userLenderSettings?.tax_rate;

  // The sales tax rate is dealer-specific (not lender-specific). It is confirmed once per browser
  // session, then reused for every lender. A new session (closed/reopened tab) prompts again with
  // the value pre-filled from the DB.
  const [isShow, setIsShow] = useState(() => !isSalesTaxRateConfirmed());
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const hasContinuedRef = useRef(false);

  const applyConfirmedState = useCallback(() => {
    setIsShow(false);
    onSave();
  }, [onSave]);

  const continueWithTaxRate = useCallback(
    (taxRate: number) => {
      commonSetValue(FEES_AND_TAXES_FIELD_NAMES.SALES_TAX_RATE, taxRate);
      applyConfirmedState();
    },
    [commonSetValue, applyConfirmedState],
  );

  const onSubmit = useCallback(async () => {
    setIsSubmitDisabled(true);
    const taxRate = getValues().lenderSettingsForm.taxRate;

    await lenderApi.setUserLenderSettings({ tax_rate: taxRate });
    dispatch(setUserLenderSettings({ tax_rate: taxRate }));
    sessionStorage.setItem(SALES_TAX_RATE_CONFIRMED_KEY, 'true');
    hasContinuedRef.current = true;
    setIsSubmitDisabled(false);

    dispatch(incrementResetCount());
    continueWithTaxRate(taxRate);
  }, [continueWithTaxRate, dispatch, getValues]);

  useEffect(() => {
    if (hasValidTaxRate(savedTaxRate)) {
      setValue(LENDER_SETTINGS_FIELDS_NAMES.TAX_RATE, savedTaxRate);
    }
  }, [setValue, savedTaxRate]);

  useEffect(() => {
    if (!isReady) hasContinuedRef.current = false;
  }, [isReady]);

  // Already confirmed earlier this session: skip the prompt and reuse the saved rate for this lender.
  useEffect(() => {
    if (!isReady || !isSalesTaxRateConfirmed() || hasContinuedRef.current || !hasValidTaxRate(savedTaxRate)) return;

    hasContinuedRef.current = true;

    const currentSalesTaxRate = commonGetValues(FEES_AND_TAXES_FIELD_NAMES.SALES_TAX_RATE);

    if (hasValidTaxRate(currentSalesTaxRate)) {
      applyConfirmedState();
    } else {
      continueWithTaxRate(savedTaxRate);
    }
  }, [isReady, savedTaxRate, continueWithTaxRate, applyConfirmedState, commonGetValues]);

  return (
    <FormProvider {...formMethods}>
      <ModalDialog
        show={isShow}
        keyboard={false}
        modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_650}
        verticallyCentered={true}
        title={t('components.lenderSetupTaxRateModal.title')}
        showCloseButton={false}
        isDisabled={isSubmitDisabled}
        submitButtonTitle={t('common.buttons.save')}
        onSubmitModal={handleSubmit(onSubmit)}
      >
        <Form>
          <Panel>
            <FormRow
              required
              fieldWidth={ColumnWidth.XL}
              label={t('components.lenderSetupTaxRateModal.fields.taxRate.label')}
              control={
                <Input
                  name={LENDER_SETTINGS_FIELDS_NAMES.TAX_RATE}
                  endAdornment={<PercentAdornment />}
                  decimalScale={3}
                  placeholder={t('components.input.placeholders.percent')}
                  rules={requiredValidator}
                  validate={validateNumberValue}
                />
              }
            />
          </Panel>
        </Form>
      </ModalDialog>
    </FormProvider>
  );
};
